import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "acceptance", title: "Acceptance" },
  { id: "eligibility", title: "Eligibility & Accounts" },
  { id: "service", title: "The Service" },
  { id: "your-content", title: "Your Content & IP" },
  { id: "trade-secrets", title: "Trade Secrets & Confidentiality" },
  { id: "license-to-us", title: "License to Us" },
  { id: "feedback", title: "Feedback" },
  { id: "our-ip", title: "Our IP" },
  { id: "aup", title: "Acceptable Use" },
  { id: "ai", title: "AI Outputs" },
  { id: "fees", title: "Fees" },
  { id: "third-parties", title: "Third-Party Services" },
  { id: "termination", title: "Termination" },
  { id: "disclaimers", title: "Disclaimers" },
  { id: "liability", title: "Limitation of Liability" },
  { id: "indemnity", title: "Indemnification" },
  { id: "law", title: "Governing Law" },
  { id: "disputes", title: "Disputes" },
  { id: "changes", title: "Changes" },
  { id: "contact", title: "Contact" },
];

const TermsOfService = () => (
  <>
    <Helmet>
      <title>Terms of Service — The Emerging Market</title>
      <meta name="description" content="The rules for using The Emerging Market, including how we treat your ideas, intellectual property, and content you submit." />
      <link rel="canonical" href="https://emrkt.co/terms" />
    </Helmet>
    <LegalLayout
      title="Terms of Service"
      description="The agreement between you and The Emerging Market when you use our platform, with specific attention to how we protect — and don't promise to protect — the ideas you submit."
      lastUpdated="June 5, 2026"
      effectiveDate="June 5, 2026"
      sections={sections}
    >
      <section id="acceptance">
        <h2>1. Acceptance of Terms</h2>
        <p>
          These Terms of Service ("Terms") govern your access to and use of the websites, applications, and
          services offered by The Emerging Market ("TEM," "we," "us"), an Alabama government affairs firm
          operating <a href="https://emrkt.co">emrkt.co</a> (the "Service"). By creating an account or using
          the Service you agree to these Terms, our <a href="/privacy">Privacy Policy</a>, our{" "}
          <a href="/acceptable-use">Acceptable Use Policy</a>, our <a href="/cookies">Cookie Policy</a>, and — where applicable — our <a href="/dpa">Data Processing Agreement</a>.
          If you do not agree, do not use the Service.
        </p>
      </section>

      <section id="eligibility">
        <h2>2. Eligibility & Accounts</h2>
        <p>
          You must be at least 13 years old (or the minimum age in your jurisdiction) and legally able to enter
          into a contract. If you use the Service on behalf of an organization, you represent that you are
          authorized to bind that organization to these Terms.
        </p>
        <p>
          You are responsible for safeguarding your account credentials and any activity under your account. We
          strongly recommend enabling multi-factor authentication. Notify us immediately at{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> of any unauthorized access.
        </p>
      </section>

      <section id="service">
        <h2>3. The Service</h2>
        <p>
          The Service helps small, community-rooted organizations track Alabama legislation, find grants and
          partners, develop ideas into projects, and receive AI-assisted coaching. Features and availability may
          change. We do not provide legal, financial, tax, or professional advice through the Service.
        </p>
      </section>

      <section id="your-content">
        <h2>4. Your Content & Intellectual Property</h2>
        <p>
          "Your Content" means anything you submit to the Service — ideas, project descriptions, attachments,
          updates, comments, profile information, AI prompts, and other materials.
        </p>
        <p>
          <strong>You retain all ownership of Your Content.</strong> TEM does not claim ownership of your ideas
          or projects. You are responsible for ensuring you have the rights to submit Your Content and that it
          does not infringe any third party's intellectual property, contractual, or privacy rights.
        </p>
        <p>You represent and warrant that:</p>
        <ul>
          <li>You own or have the necessary rights to Your Content;</li>
          <li>Your Content does not violate any law or third-party right;</li>
          <li>You have authority to grant the license below.</li>
        </ul>
      </section>

      <section id="trade-secrets">
        <h2>5. Trade Secrets & Confidentiality</h2>
        <p>
          <strong>Read this carefully.</strong> The Service includes both private and public surfaces. Content
          you mark <strong>private</strong> (e.g., private ideas, drafts, your Account Settings, direct
          coaching sessions) is intended for you and people you explicitly share it with, and is protected by
          row-level security and access controls. Content you choose to make <strong>public</strong> (public
          ideas, public profile fields, comments, affiliate listings) is, by definition, no longer
          confidential — it is published.
        </p>
        <p>
          Use of the Service <strong>does not create a non-disclosure agreement</strong> between you and TEM,
          other users, partners, or affiliates. If you have an idea, invention, formula, customer list, source
          code, or other genuinely confidential information that you consider a trade secret:
        </p>
        <ul>
          <li>Do not post it publicly on the Service.</li>
          <li>Consider seeking formal protection (e.g., provisional patent, copyright registration, written NDA with specific recipients) <em>before</em> sharing.</li>
          <li>Do not submit trade secrets, confidential information, or proprietary materials belonging to a third party (including a current or former employer) without authorization.</li>
        </ul>
        <p>
          While we take reasonable steps to secure private content, no online service can guarantee absolute
          confidentiality. You accept this risk when you use the Service.
        </p>
      </section>

      <section id="license-to-us">
        <h2>6. Limited License You Grant to TEM</h2>
        <p>
          You grant TEM a worldwide, non-exclusive, royalty-free license to host, store, reproduce, transmit,
          display, adapt (for formatting, indexing, and AI processing), and distribute Your Content solely as
          needed to operate, secure, improve, and provide the Service to you and the audiences you designate.
          For content you make public, this license extends to displaying it to the public via the Service. For
          content you make private, the license is limited to what is necessary to deliver the Service to you.
        </p>
        <p>
          We may use de-identified, aggregated information derived from Your Content to improve the Service and
          measure usage. Aggregated data does not identify you or reveal the substance of your private ideas.
        </p>
        <p>
          <strong>Research aggregates (opt-in).</strong> Separately, if you turn on
          "Research aggregates" in Account Settings or check the box at signup, you allow TEM to
          include de-identified counts and signals derived from Your Content (e.g., sector,
          county, pipeline stage, grant-fit distributions) in published research reports and the
          Intelligence-tier aggregates API. This permission is off by default, is separate from
          the operational license above, and you can revoke it at any time from Account Settings.
          Reports already published cannot be retroactively recomputed.
        </p>
        <p>
          The license ends when you delete Your Content or your account, except for (a) backup copies during
          normal rotation, (b) public content others have copied, quoted, or referenced before deletion, and
          (c) anonymized analytics already generated.
        </p>
      </section>

      <section id="feedback">
        <h2>7. Feedback</h2>
        <p>
          If you send us suggestions, bug reports, or feature requests ("Feedback"), you grant TEM a perpetual,
          irrevocable, royalty-free license to use that Feedback for any purpose without obligation to you.
        </p>
      </section>

      <section id="our-ip">
        <h2>8. Our Intellectual Property</h2>
        <p>
          The Service, including its software, design, brand, logos, copy, datasets we curate, and the
          "Emerging Market" and "emrkt" marks, is owned by TEM or its licensors and protected by intellectual
          property laws. Except for the limited rights expressly granted to you to use the Service, no rights
          are transferred.
        </p>
      </section>

      <section id="aup">
        <h2>9. Acceptable Use</h2>
        <p>
          You agree to follow our <a href="/acceptable-use">Acceptable Use Policy</a>, including its rules
          against copyright infringement, idea theft, abuse, scraping, and unlawful content. Violation may
          result in content removal, suspension, or termination.
        </p>
      </section>

      <section id="ai">
        <h2>10. AI Outputs</h2>
        <p>
          AI-generated outputs (coaching responses, scores, grant matches, summaries) are provided for
          informational purposes only, may be inaccurate or incomplete, and should be independently verified
          before you rely on them for a funding application, legal decision, or any other consequential action.
          You are responsible for how you use AI outputs.
        </p>
      </section>

      <section id="fees">
        <h2>11. Fees</h2>
        <p>
          Most current features are offered free of charge. We may introduce paid tiers in the future; any
          changes affecting paid features will be communicated before they take effect. Government affairs
          engagements with TEM as a firm are governed by a separate written agreement.
        </p>
      </section>

      <section id="third-parties">
        <h2>12. Third-Party Services and Links</h2>
        <p>
          The Service integrates with or links to third-party services (e.g., Google sign-in, LinkedIn, RSS
          feeds, government data sources, partner websites). Your use of those services is governed by their
          own terms and policies. We are not responsible for third-party content or practices.
        </p>
      </section>

      <section id="termination">
        <h2>13. Termination</h2>
        <p>
          You may stop using the Service at any time and delete your account from <a href="/account">Account
          Settings</a>. We may suspend or terminate your access if you breach these Terms or the Acceptable
          Use Policy, if required by law, or to protect users or the Service. Sections that by their nature
          should survive termination (IP, disclaimers, liability, indemnity, governing law, disputes) will
          survive.
        </p>
      </section>

      <section id="disclaimers">
        <h2>14. Disclaimers</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
          IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
          WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT GRANT,
          LEGISLATIVE, OR PARTNER DATA SURFACED THROUGH THE SERVICE IS CURRENT, COMPLETE, OR ACCURATE.
        </p>
      </section>

      <section id="liability">
        <h2>15. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TEM AND ITS AFFILIATES, OFFICERS, AND EMPLOYEES WILL NOT BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST
          PROFITS, REVENUES, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATING TO YOUR USE OF THE
          SERVICE. OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER
          OF (A) THE AMOUNTS YOU PAID US IN THE TWELVE MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S.
          DOLLARS.
        </p>
      </section>

      <section id="indemnity">
        <h2>16. Indemnification</h2>
        <p>
          You will indemnify and hold harmless TEM from any claims, damages, liabilities, and expenses
          (including reasonable attorneys' fees) arising out of (a) Your Content, (b) your use of the Service,
          (c) your violation of these Terms or the Acceptable Use Policy, or (d) your violation of any third
          party's rights, including intellectual property and privacy rights.
        </p>
      </section>

      <section id="law">
        <h2>17. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of Alabama, USA, without regard to its conflict-of-
          laws rules. The U.N. Convention on Contracts for the International Sale of Goods does not apply.
        </p>
      </section>

      <section id="disputes">
        <h2>18. Disputes</h2>
        <p>
          The state and federal courts located in Mobile County, Alabama have exclusive jurisdiction over any
          dispute arising out of or relating to these Terms or the Service, and you consent to personal
          jurisdiction there. You and TEM waive any right to a jury trial. Any claim must be filed within one
          (1) year of the events giving rise to it or be permanently barred, except where prohibited by law.
        </p>
      </section>

      <section id="changes">
        <h2>19. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Material changes will be communicated by email or
          in-product notice and take effect on the date posted. Your continued use of the Service after that
          date constitutes acceptance.
        </p>
      </section>

      <section id="contact">
        <h2>20. Contact</h2>
        <p>
          The Emerging Market<br />
          Email: <a href="mailto:info@emrkt.co">info@emrkt.co</a><br />
          Mobile, Alabama, USA
        </p>
      </section>
    </LegalLayout>
  </>
);

export default TermsOfService;